import React from 'react'

import { PoweredByVera } from 'components/UI'

import { Stack, Box } from '@mui/material'
import { glassSx } from 'utils/glass'

interface FooterProps {
  /**
   * VERA-644 (TD-066): Enterprise white-label entitlement — when true, the
   * certificate's company has enabled + is entitled to
   * `certificate_trigger_settings.hide_powered_by`, so the "Powered by
   * Vera" attribution must not render.
   */
  hidePoweredBy?: boolean
  /**
   * Pin the attribution to the bottom of the viewport instead of letting it sit at the
   * end of the page. Used by surfaces with infinite scroll (the certificate Scans list),
   * where page-level paging otherwise pushes the footer down indefinitely so it's never
   * seen (VERA-476).
   *
   * Deliberately sticky rather than giving the list its own bordered scroll box: that
   * list already lives inside a tab inside a scrolling product page, so an inner
   * scroller would make three nested scroll contexts and touch scroll-chaining gets
   * unpredictable about which one moves.
   */
  sticky?: boolean
  /**
   * Drop the top padding from 28 to 16.
   *
   * The 28 buys SEPARATION FROM CONTENT — see the note on `padding` below; at 12 the
   * attribution read as the last paragraph of whatever prose it followed. That argument
   * needs prose above it to be true, and on the certificate page's ACCESS GATE there is
   * none: the thing above is a card with its own 16px bottom margin, and under it a
   * decorative blurred placeholder that is allowed to collapse to nothing. So the
   * attribution is already 16px clear of a bordered card edge, and the extra 12 is
   * spent making a one-screen page not fit on one screen.
   *
   * Deliberately NOT the default. Every other surface DOES put prose immediately above
   * this, which is the case the 28 was measured against.
   */
  compact?: boolean
}

/** Scroll past this before the footer pins, so a page at rest looks untouched.
 *  Deliberately well past the first row: at 80px the bar snapped in almost as soon
 *  as you started moving, which read as twitchy. ~a quarter-viewport in, the reader
 *  is clearly scrolling the list rather than nudging the page. */
const STICKY_ENGAGE_PX = 240

export const Footer = ({ hidePoweredBy, sticky, compact }: FooterProps = {}) => {
  // Pin only once the reader is ACTUALLY scrolling. Sticky-from-the-top put a pinned
  // bar over the list before anything had scrolled, which just covers content — the
  // same reason the top controls bar only draws its divider once stuck. Scrolling back
  // to the top releases it and the page returns to its normal shape (VERA-476).
  const [engaged, setEngaged] = React.useState(false)

  React.useEffect(() => {
    if (!sticky) {
      setEngaged(false)
      return
    }
    const check = () => setEngaged(window.scrollY > STICKY_ENGAGE_PX)
    check()
    window.addEventListener('scroll', check, { passive: true })
    return () => window.removeEventListener('scroll', check)
  }, [sticky])

  const isPinned = Boolean(sticky) && engaged

  if (hidePoweredBy) {
    // Keep the footer's bottom breathing room so the last content (Details) isn't
    // flush against the bottom of the page when the "Powered by Vera" badge is
    // hidden — mirrors the padding the attributed footer would have occupied.
    return <Box sx={{ height: 56 }} aria-hidden />
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      sx={theme => ({
        // Was '24px 16px 36px' — 60px of vertical space around one line of
        // attribution, more air than any real content on the page gets. The
        // bottom is 16 because the LAYOUT already reserves the floating nav's
        // 88px below this; the old 36 was stacking on top of that reservation.
        //
        // The TOP went back up to 28: at 12 the attribution sat as close to the
        // description above it as that description's own lines sat to each
        // other, so it read as the last paragraph of the content rather than as
        // a footer. Separation is the only thing marking it as not-content now
        // that it is no longer competing on size.
        padding: isPinned
          ? '20px 16px'
          : compact
            ? '16px'
            : '28px 16px 16px',
        ...(isPinned && {
          position: 'sticky',
          bottom: 0,
          zIndex: 1,
          // Opaque, or scrolling rows show through the pinned bar.
          /* Glass, like every other pinned bar (utils/glass). A solid fill on a
             bar that content scrolls under reads as a separate panel bolted to the
             bottom; the blur is what says it is floating OVER the page. */
          ...glassSx(theme),
          borderTop: `1px solid ${theme.palette.grey[200]}`,
          // Clear the iOS Safari toolbar / home indicator. 20px (not 12) so the pinned
          // bar keeps some breathing room rather than hugging the wordmark.
          paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
        }),
      })}
    >
      <PoweredByVera />
    </Stack>
  )
}
